// THE FIRST RUN — "Get started", the Fly start screen's last part on a NEW
// workspace (functions/assistantFlyFirstRun.js; fly docs/FUNNEL.md §4 G5).
// Four steps that mirror the activation rule — invite your team · add your
// first records · publish your website · ask your workspace — each a card of
// the tiles' family with its live progress and ONE action; a done step wears
// the plain tick and is no control. The head names the part, says one line
// (what Fly already made, when the person came from Fly) and hides it for this
// workspace (the X).
//
// THE START SCREEN'S LAST PART, on purpose: the read lands after the screen
// paints, so a part ABOVE the tiles would push them down under the person's
// pointer (Rule 66); last, it moves nothing when it comes or goes. While the
// steps are on screen an activation shows the done state in the same box.
//
// It holds no logic: every value comes from functions/assistantFlyFirstRun.js
// through `el.call`. Hidden — and it asks nothing — while the start screen is
// hidden, until the shell's SDK has the activation read, and wherever the
// answer says the workspace is activated or out of its window.
export const AssistantFlyFirstRun = {
  tag: 'section',
  dataFlyFirstRun: (el, s) => el.call('assistantFlyFirstRunView', s.root).mode || null,
  ariaLabel: (el, s) => el.call('assistantFlyFirstRunView', s.root).title || null,
  hide: (el, s) => !el.call('assistantFlyFirstRunView', s.root).show,
  flow: 'y',
  gap: 'Z',
  minWidth: '0',
  // asked while the start screen SHOWS: at mount, then whenever it comes
  // back, the workspace changes or becomes ready, and on the shell's 30 s
  // tick (the read itself keeps a minute between asks)
  onRender: (el, s) => el.call('assistantFlyFirstRunPrime', el, s),
  stateDeps: [
    (el, s) => !!el.call('assistantFlyStartShown', s.root),
    (el, s) => !!(s.root && s.root.workspaceReady),
    (el, s) =>
      s.root && s.root.workspace ? String(s.root.workspace.id || s.root.workspace._id || '') : '',
    (el, s) => (s.root ? s.root._meetingInviteTick : null)
  ],
  onStateUpdate: (el, s) => el.call('assistantFlyFirstRunPrime', el, s),

  FirstRunHead: {
    flow: 'x',
    align: 'flex-start space-between',
    gap: 'A',
    minWidth: '0',
    FirstRunIntro: {
      flow: 'y',
      gap: 'X',
      minWidth: '0',
      AssistantFlyStartPartTitle: {
        text: (el, s) => el.call('assistantFlyFirstRunView', s.root).title
      },
      FirstRunLead: {
        tag: 'p',
        dataFlyFirstRunLead: true,
        margin: '0',
        maxWidth: 'H',
        fontSize: 'Z2',
        lineHeight: '1.5',
        color: 'caption',
        text: (el, s) => el.call('assistantFlyFirstRunView', s.root).lead
      }
    },
    // the person's own choice for THIS workspace, kept on their record (the
    // brand CloseButton takes a flat name before its own default)
    CloseButton: {
      dataFlyFirstRunHide: true,
      ariaLabel: (el, s) => el.call('assistantFlyFirstRunView', s.root).hideLabel || null,
      onClick: (e, el) => el.call('assistantFlyFirstRunDismiss')
    }
  },

  // THE PRESETS' LEAD-IN — one line, not a step (AssistantFlyFirstRunLeadIn).
  AssistantFlyFirstRunLeadIn: {},

  // The cards on whole rows, never a hole: as many abreast as there are steps
  // (four in a team workspace, three in a personal one — the answer's
  // `applies`); from the tablet step four become two by two and three stay
  // one row; one on a small phone. Every row as tall as its tallest card.
  FirstRunSteps: {
    role: 'list',
    dataFlyFirstRunSteps: true,
    display: 'grid',
    style: (el, s) => {
      const n = el.call('assistantFlyFirstRunView', s.root).cols || 4
      return {
        '--fly-first-run-cols': String(n),
        '--fly-first-run-cols-mid': String(n === 4 ? 2 : n)
      }
    },
    gridTemplateColumns: 'repeat(var(--fly-first-run-cols, 4), minmax(0, 1fr))',
    gridAutoRows: '1fr',
    gap: 'Z',
    minWidth: '0',
    '@tabletS': { gridTemplateColumns: 'repeat(var(--fly-first-run-cols-mid, 2), minmax(0, 1fr))' },
    '@mobileS': { gridTemplateColumns: 'minmax(0, 1fr)' },
    children: (el, s) => el.call('assistantFlyFirstRunView', s.root).steps,
    childrenAs: 'state',
    childExtends: 'AssistantFlyFirstRunStep'
  }
}

// ONE STEP — a list item holding its face: the press while the step is open
// (the whole card is its one action, the start screen's own press), the same
// face without a control once the server says it is done. The face's words
// are its name: the title, the line, the count, where it goes (the glyphs are
// hidden from assistive tech).
export const AssistantFlyFirstRunStep = {
  role: 'listitem',
  display: 'flex',
  minWidth: '0',
  dataFlyFirstRunStep: (el, s) => (s && s.step) || null,
  dataDone: (el, s) => (s && s.done ? 'true' : null),
  AssistantFlyFirstRunPress: {
    hide: (el, s) => !s || !!s.done,
    onClick: (e, el, s) => el.call('assistantFlyFirstRunOpen', s, el)
  },
  AssistantFlyFirstRunFace: {
    hide: (el, s) => !s || !s.done
  }
}

// An open step's press: the start screen's own press (its button, its hover
// and press washes, its focus ring) wearing the step's face.
export const AssistantFlyFirstRunPress = {
  extends: ['AssistantFlyStartPress', 'AssistantFlyFirstRunFace']
}

// THE FACE of a step: its glyph (the plain tick once done) and title, one
// line of what to do, then the server's count (2 of 5) and where the press
// goes. On the home's sky the house card (glass); on a sheet the solid card —
// the start screen press's own ground.
export const AssistantFlyFirstRunFace = {
  flex: '1',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: 'A',
  minWidth: '0',
  padding: 'A',
  round: 'radiusCard',
  boxSizing: 'border-box',
  background: (el) =>
    el.call('assistantFlyOnHome', el)
      ? 'var(--home-card-bg, var(--theme-card-background))'
      : 'cardSolid',
  color: 'title',
  textAlign: 'left',
  FaceHead: {
    flow: 'y',
    gap: 'Y',
    minWidth: '0',
    FaceTitleRow: {
      flow: 'x',
      align: 'center',
      gap: 'Y',
      minWidth: '0',
      Icon: {
        ariaHidden: 'true',
        name: (el, s) => (s && s.icon) || 'magicstar',
        boxSize: 'iconSm',
        flexShrink: '0',
        color: (el, s) => (s && s.done ? 'title' : 'caption')
      },
      FaceTitle: {
        tag: 'span',
        minWidth: '0',
        fontSize: 'A',
        fontWeight: '500',
        lineHeight: '1.3',
        overflowWrap: 'anywhere',
        text: (el, s) => (s && s.title) || ''
      }
    },
    FaceHint: {
      tag: 'span',
      minWidth: '0',
      fontSize: 'Z2',
      lineHeight: '1.45',
      color: 'caption',
      overflowWrap: 'anywhere',
      text: (el, s) => (s && s.hint) || ''
    }
  },
  FaceFoot: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    minWidth: '0',
    FaceProgress: {
      tag: 'span',
      dataFlyFirstRunProgress: true,
      flexShrink: '0',
      fontSize: 'Z',
      color: 'caption',
      fontVariantNumeric: 'tabular-nums',
      text: (el, s) => (s && s.progress) || ''
    },
    FaceAction: {
      flow: 'x',
      align: 'center',
      gap: 'Y',
      minWidth: '0',
      marginLeft: 'auto',
      fontSize: 'Z2',
      fontWeight: '500',
      FaceActionText: {
        tag: 'span',
        minWidth: '0',
        overflowWrap: 'anywhere',
        text: (el, s) => (s && s.action) || ''
      },
      Icon: {
        ariaHidden: 'true',
        name: 'arrowRight',
        boxSize: 'iconSm',
        flexShrink: '0',
        hide: (el, s) => !s || !!s.done
      }
    }
  }
}

// THE PRESETS' LEAD-IN — one line above the steps, NOT a step (no tick, not
// counted: the steps stay the server's rule): a workspace with no preset yet,
// for a person who may apply one, is offered the App Store's presets row —
// "Set up for your work — start from a preset" (functions/firstRunPresetLine.js
// decides; the block's X hides it with the block). The start screen's own
// press, as a slim row: a glyph, the line, the arrow of where it goes.
export const AssistantFlyFirstRunLeadIn = {
  extends: 'AssistantFlyStartPress',
  dataFlyFirstRunPreset: true,
  display: 'flex',
  alignItems: 'center',
  gap: 'Z',
  padding: 'Z A',
  round: 'radiusCard',
  hide: (el, s) => !el.call('assistantFlyFirstRunPresetLine', s.root),
  // the registry is read while the start screen shows (the read settles
  // whether this host can apply presets at all)
  onRender: (el, s) => el.call('assistantFlyFirstRunPresetPrime', el, s),
  stateDeps: [
    (el, s) => !!el.call('assistantFlyStartShown', s.root),
    (el, s) =>
      s.root && s.root.workspace ? String(s.root.workspace.id || s.root.workspace._id || '') : ''
  ],
  onStateUpdate: (el, s) => el.call('assistantFlyFirstRunPresetPrime', el, s),
  onClick: (e, el) => el.call('assistantFlyFirstRunPresetOpen'),
  Icon: {
    ariaHidden: 'true',
    name: 'layers',
    boxSize: 'iconSm',
    flexShrink: '0',
    color: 'caption'
  },
  LeadInText: {
    tag: 'span',
    flex: '1',
    minWidth: '0',
    fontSize: 'Z2',
    fontWeight: '500',
    lineHeight: '1.4',
    overflowWrap: 'anywhere',
    text: '{{ workspace.firstRun.presetLine | polyglot }}'
  },
  Icon_1: {
    ariaHidden: 'true',
    name: 'arrowRight',
    boxSize: 'iconSm',
    flexShrink: '0',
    color: 'caption'
  }
}
